// Per-app gradient palette — keeps each App visually distinct so the
// AppLauncher / Sidebar grid view doesn't read as a wall of identical tiles.
// Source of truth for app identity color, shared between Sidebar.tsx and
// AppLauncherItem.tsx. Keys are launcher item ids OR sidebar nav keys
// (they overlap intentionally — same app, same color, wherever it appears).
//
// Brand v2 palette (locked 2026-05-11, see docs/BRAND.md):
//   - Teal     #14b8a6 → #2dd4bf  (primary system, "filled brand color")
//   - Cyan     #06b6d4 → #22d3ee  (info / AI / comms)
//   - Emerald  #10b981 → #34d399  (time / growth / sync)
//   - Blue     #3b82f6 → #60a5fa  (knowledge / structure)
//   - Amber    #f59e0b → #fbbf24  (data / warn-tone fills)
//   - Rose     #ec4899 → #f472b6  (creative / design)
//   - Slate    #64748b → #94a3b8  (system / admin / utility)
//   - Red      #ef4444 → #f87171  (media / destructive)
//
// FORBIDDEN as fill (Brand v2):
//   - Orange #f97316 — LINE-ONLY signature accent (spine, slash). Never fill.
//   - Purple/Indigo (#6366f1, #8b5cf6, #a855f7, etc) — no longer in palette.

export const APP_GRADIENTS: Record<string, [string, string]> = {
  // ── Home / launcher ──
  dashboard:       ["#14b8a6", "#2dd4bf"],  // teal — primary system home
  appLauncher:     ["#64748b", "#94a3b8"],  // slate — utility

  // ── Project / structure ──
  layers:          ["#3b82f6", "#60a5fa"],  // blue
  vcards:          ["#14b8a6", "#2dd4bf"],  // teal — Editor + Entities merged (DEC-0448)
  editor:          ["#10b981", "#34d399"],  // emerald — legacy key
  storyMap:        ["#ec4899", "#f472b6"],  // rose — narrative/creative

  // ── AI ──
  aiHub:           ["#06b6d4", "#22d3ee"],  // cyan
  cliAi:           ["#06b6d4", "#22d3ee"],
  aiAdvisors:      ["#06b6d4", "#22d3ee"],
  agents:          ["#06b6d4", "#22d3ee"],
  aiKnowledge:     ["#06b6d4", "#22d3ee"],
  semanticBrain:   ["#06b6d4", "#22d3ee"],

  // ── Data / entities — amber (warm but not the line-only orange) ──
  entities:        ["#f59e0b", "#fbbf24"],
  entityFields:    ["#f59e0b", "#fbbf24"],
  fieldTemplates:  ["#f59e0b", "#fbbf24"],
  databases:       ["#f59e0b", "#fbbf24"],
  grid:            ["#f59e0b", "#fbbf24"],
  feeds:           ["#f59e0b", "#fbbf24"],

  // ── Comms / templates ──
  comms:           ["#06b6d4", "#22d3ee"],
  templates:       ["#06b6d4", "#22d3ee"],
  emailTemplates:  ["#06b6d4", "#22d3ee"],
  phoneTemplates:  ["#10b981", "#34d399"],

  // ── Documents / knowledge ──
  documents:       ["#3b82f6", "#60a5fa"],  // blue — knowledge
  wiki:            ["#3b82f6", "#60a5fa"],
  wikiPages:       ["#3b82f6", "#60a5fa"],
  courses:         ["#3b82f6", "#60a5fa"],
  libraries:       ["#3b82f6", "#60a5fa"],
  prompts:         ["#06b6d4", "#22d3ee"],

  // ── Media — red (only allowed "loud" hue per Brand v2 signal palette) ──
  vclip:           ["#ef4444", "#f87171"],
  vcloudVideo:     ["#ef4444", "#f87171"],
  vcloudSound:     ["#f59e0b", "#fbbf24"],  // audio = warm amber

  // ── Time / planning ──
  weeklyPlanner:   ["#10b981", "#34d399"],  // emerald
  roadmap:         ["#10b981", "#34d399"],
  functionalMap:   ["#10b981", "#34d399"],
  plan:            ["#3b82f6", "#60a5fa"],

  // ── Design / talent ──
  designSystem:    ["#ec4899", "#f472b6"],
  slides:          ["#ec4899", "#f472b6"],
  matching:        ["#f59e0b", "#fbbf24"],  // warm data
  talentSearch:    ["#14b8a6", "#2dd4bf"],
  skills:          ["#14b8a6", "#2dd4bf"],
  skillsStore:     ["#14b8a6", "#2dd4bf"],

  // ── Files / cloud ──
  vcloud:          ["#06b6d4", "#22d3ee"],
  vcloudFiles:     ["#06b6d4", "#22d3ee"],
  vcloudImages:    ["#14b8a6", "#2dd4bf"],
  vcloudPersonal:  ["#64748b", "#94a3b8"],

  // ── Visual tools ──
  iconLibrary:     ["#f59e0b", "#fbbf24"],

  // ── Integrations / connections ──
  integrations:    ["#64748b", "#94a3b8"],
  myConnections:   ["#64748b", "#94a3b8"],
  appStore:        ["#3b82f6", "#60a5fa"],
  sync:            ["#10b981", "#34d399"],

  // ── Vault / secrets ──
  vault:           ["#64748b", "#94a3b8"],
  vaultPasswords:  ["#64748b", "#94a3b8"],
  vaultSecrets:    ["#475569", "#64748b"],

  // ── System / admin ──
  control:         ["#64748b", "#94a3b8"],
  architecture:    ["#64748b", "#94a3b8"],
  admin:           ["#64748b", "#94a3b8"],
  serverHealth:    ["#10b981", "#34d399"],
  audit:           ["#f59e0b", "#fbbf24"],
  import:          ["#10b981", "#34d399"],
  bookmarks:       ["#f59e0b", "#fbbf24"],
  readList:        ["#f59e0b", "#fbbf24"],
  automations:     ["#f59e0b", "#fbbf24"],
  settings:        ["#64748b", "#94a3b8"],
};

/**
 * Resolve an app gradient by id/key. Falls back to a neutral slate
 * gradient if the id is unknown — so new apps don't crash, they just
 * look generic until added to the map.
 */
export function getAppGradient(id: string): [string, string] {
  return APP_GRADIENTS[id] ?? ["#475569", "#64748b"];
}
